import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usersGraphqlService, UserGroupDashboard } from '@/services/api/users/usersGraphqlService';
import { useErrorHandler } from '@/hooks/useErrorHandler';

export interface UseUserGroupsResult {
  userGroups: UserGroupDashboard[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  entriesPerPage: number;
  filterType: string;
  searchValue: string;
  setCurrentPage: (page: number) => void;
  setEntriesPerPage: (entries: number) => void;
  setFilterType: (type: string) => void;
  setSearchValue: (value: string) => void;
  handleSearch: () => void;
  clearSearch: () => void;
  refetch: () => void;
}

export const useUserGroups = (): UseUserGroupsResult => {
  const { hasuraToken } = useAuth();
  const { handleError } = useErrorHandler();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL params
  const [userGroups, setUserGroups] = useState<UserGroupDashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  
  const [currentPage, setCurrentPageState] = useState(() => {
    const page = searchParams.get('groupPage');
    return page ? parseInt(page, 10) : 1;
  });
  
  const [entriesPerPage, setEntriesPerPageState] = useState(() => {
    const entries = searchParams.get('groupEntries');
    return entries ? parseInt(entries, 10) : 50;
  });
  
  const [filterType, setFilterType] = useState(() => {
    return searchParams.get('groupFilterType') || 'user_group_name';
  });
  
  const [searchValue, setSearchValue] = useState(() => {
    return searchParams.get('groupSearch') || '';
  });
  
  const [activeSearch, setActiveSearch] = useState<{ type: string; value: string } | null>(() => {
    const search = searchParams.get('groupSearch');
    const filter = searchParams.get('groupFilterType');
    return search && filter ? { type: filter, value: search } : null;
  });

  const totalPages = Math.ceil(totalCount / entriesPerPage);

  const fetchUserGroups = useCallback(async () => {
    if (!hasuraToken) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const orderBy = [{ user_group_id: 'desc' }];
      const whereClause: Record<string, any> = {};
      let hasFilters = false;

      // Build where clause from active search
      if (activeSearch && activeSearch.value.trim()) {
        hasFilters = true;
        
        if (activeSearch.type === 'user_group_name') {
          whereClause.user_group_name = { _ilike: `%${activeSearch.value}%` };
        } else if (activeSearch.type === 'user_count') {
          whereClause.user_count = { _eq: parseInt(activeSearch.value) };
        } else if (activeSearch.type === 'created_datetime') {
          whereClause.created_datetime = { _gte: `${activeSearch.value}T00:00:00`, _lte: `${activeSearch.value}T23:59:59` };
        } else if (activeSearch.type === 'updated_datetime') {
          whereClause.updated_datetime = { _gte: `${activeSearch.value}T00:00:00`, _lte: `${activeSearch.value}T23:59:59` };
        }
      }

      // First, get the total count
      const countResult = hasFilters 
        ? await usersGraphqlService.searchUserGroupsDashboard(
            hasuraToken,
            1,
            0,
            whereClause,
            orderBy
          )
        : await usersGraphqlService.getUserGroupsDashboard(
            hasuraToken,
            1,
            0,
            orderBy
          );

      if (!countResult.success) {
        handleError(countResult.errors?.[0] || 'Failed to fetch user groups count');
        setUserGroups([]);
        setTotalCount(0);
        return;
      }

      const count = countResult.data?.pep_vw_user_group_dashboard_aggregate?.aggregate?.count || 0;
      setTotalCount(count);

      // Then fetch all records
      if (count > 0) {
        const result = hasFilters
          ? await usersGraphqlService.searchUserGroupsDashboard(
              hasuraToken,
              count,
              0,
              whereClause,
              orderBy
            )
          : await usersGraphqlService.getUserGroupsDashboard(
              hasuraToken,
              count,
              0,
              orderBy
            );

        if (result.success && result.data) {
          setUserGroups(result.data.pep_vw_user_group_dashboard || []);
        } else {
          handleError(result.errors?.[0] || 'Failed to fetch user groups');
          setUserGroups([]);
        }
      } else {
        setUserGroups([]);
      }
    } catch (err) {
      handleError(err, 'Error fetching user groups');
      setUserGroups([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [hasuraToken, activeSearch, handleError]);

  useEffect(() => {
    fetchUserGroups();
  }, [fetchUserGroups]);

  const handleSearch = useCallback(() => {
    setActiveSearch({ type: filterType, value: searchValue });
    setCurrentPageState(1);
    
    // Update URL params
    const params = Object.fromEntries(searchParams.entries());
    params.groupPage = '1';
    params.groupEntries = entriesPerPage.toString();
    params.groupFilterType = filterType;
    params.groupSearch = searchValue;
    setSearchParams(params);
  }, [filterType, searchValue, entriesPerPage, searchParams, setSearchParams]);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setActiveSearch(null);
    setCurrentPageState(1);
    
    // Clear filter-related URL params
    const params = Object.fromEntries(searchParams.entries());
    params.groupPage = '1';
    params.groupEntries = entriesPerPage.toString();
    delete params.groupFilterType;
    delete params.groupSearch;
    setSearchParams(params);
  }, [entriesPerPage, searchParams, setSearchParams]);

  const handleSetCurrentPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPageState(page);
      // Update URL params
      const params = Object.fromEntries(searchParams.entries());
      params.groupPage = page.toString();
      setSearchParams(params);
    }
  }, [totalPages, searchParams, setSearchParams]);

  const handleSetEntriesPerPage = useCallback((entries: number) => {
    setEntriesPerPageState(entries);
    setCurrentPageState(1);
    // Update URL params
    const params = Object.fromEntries(searchParams.entries());
    params.groupPage = '1';
    params.groupEntries = entries.toString();
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  return {
    userGroups,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    entriesPerPage,
    filterType,
    searchValue,
    setCurrentPage: handleSetCurrentPage,
    setEntriesPerPage: handleSetEntriesPerPage,
    setFilterType,
    setSearchValue,
    handleSearch,
    clearSearch,
    refetch: fetchUserGroups,
  };
};